import React, { useEffect, useMemo, useState } from 'react';
import {
  Field,
  Input,
  Radio,
  RadioGroup,
  Button,
  Text,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import { Info20Regular } from '@fluentui/react-icons';
import BaseDialog from '../BaseDialog';

/**
 * Order-level discount dialog. Lets staff express a discount as either a
 * percentage of the subtotal or a fixed dollar amount, with a live preview of
 * the resulting subtotal.
 *
 * STATUS: stubbed UI — a planned feature. The "Apply" action is intentionally
 * disabled and nothing is persisted yet. When wired up, the agreed mechanism
 * is to push the discount to the Booqable order as a dedicated discount LINE
 * ITEM (works for both % and fixed $, and reflects in Booqable's totals and
 * the generated PDFs) rather than a coupon or a TWM-only record.
 */
interface Props {
  isOpen: boolean;
  onClose: () => void;
  /** Discountable subtotal in cents — drives the preview math. */
  subtotalInCents: number;
}

type DiscountMode = 'percentage' | 'fixed';

const useStyles = makeStyles({
  body: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('16px'),
  },
  // Neutral (not warning-styled) info bar — this communicates "not built yet",
  // which is informational, not a warning state.
  plannedNote: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('8px'),
    ...shorthands.padding('10px', '12px'),
    backgroundColor: tokens.colorNeutralBackground3,
    ...shorthands.borderRadius('4px'),
    color: tokens.colorNeutralForeground2,
    fontSize: '13px',
  },
  preview: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('6px'),
    ...shorthands.padding('12px'),
    backgroundColor: tokens.colorNeutralBackground2,
    ...shorthands.borderRadius('4px'),
  },
  previewRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  previewDivider: {
    height: '1px',
    backgroundColor: tokens.colorNeutralStroke2,
    ...shorthands.margin('2px', '0'),
  },
});

const fmtUsd = (cents: number) =>
  ((cents || 0) / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

export const DiscountDialog: React.FC<Props> = ({ isOpen, onClose, subtotalInCents }) => {
  const styles = useStyles();
  const [mode, setMode] = useState<DiscountMode>('percentage');
  const [amountStr, setAmountStr] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setMode('percentage');
    setAmountStr('');
  }, [isOpen]);

  // Resolve the discount amount the entered value represents, clamped so a
  // discount can never exceed the subtotal or go negative.
  const discountCents = useMemo(() => {
    const n = parseFloat(amountStr);
    if (!Number.isFinite(n) || n <= 0) return 0;
    const raw =
      mode === 'percentage'
        ? Math.round((subtotalInCents * n) / 100)
        : Math.round(n * 100);
    return Math.max(0, Math.min(raw, subtotalInCents));
  }, [amountStr, mode, subtotalInCents]);

  const newSubtotalCents = subtotalInCents - discountCents;

  return (
    <BaseDialog
      isOpen={isOpen}
      onClose={onClose}
      title="Add a discount"
      maxWidth="460px"
      actions={
        <>
          <Button appearance="secondary" onClick={onClose}>
            Cancel
          </Button>
          {/* Disabled until the Booqable discount-line wiring lands. */}
          <Button appearance="primary" disabled title="Discounts aren't applied to orders yet">
            Apply
          </Button>
        </>
      }
    >
      <div className={styles.body}>
        <div className={styles.plannedNote}>
          <Info20Regular />
          <span>Discounts aren't applied to orders yet — this is a planned feature.</span>
        </div>

        <Field label="Discount type">
          <RadioGroup
            layout="horizontal"
            value={mode}
            onChange={(_, d) => {
              setMode(d.value as DiscountMode);
              // Clear the amount on a type switch — a value entered as a
              // percentage rarely makes sense as a dollar amount (and vice
              // versa), so start fresh rather than carry it over.
              setAmountStr('');
            }}
          >
            <Radio value="percentage" label="Percentage" />
            <Radio value="fixed" label="Fixed amount" />
          </RadioGroup>
        </Field>

        <Field label={mode === 'percentage' ? 'Percentage off' : 'Amount off (USD)'}>
          <Input
            size="large"
            value={amountStr}
            onChange={(_, d) => {
              // Numbers only — strip anything that isn't a digit or a decimal
              // point, and collapse to a single decimal point.
              let v = d.value.replace(/[^0-9.]/g, '');
              const firstDot = v.indexOf('.');
              if (firstDot !== -1) {
                v = v.slice(0, firstDot + 1) + v.slice(firstDot + 1).replace(/\./g, '');
              }
              // A percentage discount can't exceed 100 — ceiling it as the
              // user types so the field never holds an impossible value.
              if (mode === 'percentage') {
                const n = parseFloat(v);
                if (Number.isFinite(n) && n > 100) {
                  setAmountStr('100');
                  return;
                }
              }
              setAmountStr(v);
            }}
            placeholder={mode === 'percentage' ? '0' : '0.00'}
            inputMode="decimal"
            contentBefore={mode === 'fixed' ? <Text>$</Text> : undefined}
            contentAfter={mode === 'percentage' ? <Text>%</Text> : undefined}
            autoFocus
          />
        </Field>

        <div className={styles.preview}>
          <div className={styles.previewRow}>
            <Text>Subtotal</Text>
            <Text>{fmtUsd(subtotalInCents)}</Text>
          </div>
          <div className={styles.previewRow}>
            <Text>Discount</Text>
            <Text>{discountCents > 0 ? `-${fmtUsd(discountCents)}` : fmtUsd(0)}</Text>
          </div>
          <div className={styles.previewDivider} />
          <div className={styles.previewRow}>
            <Text weight="semibold">New subtotal</Text>
            <Text weight="semibold">{fmtUsd(newSubtotalCents)}</Text>
          </div>
        </div>
      </div>
    </BaseDialog>
  );
};

export default DiscountDialog;
